/**
 * Shared helpers for map "Hide without Flags" filtering.
 * Flags are computed per lease; counties/wells inherit from leases.
 */

/**
 * Count flags that belong to the selected datasets.
 * @param {Object} flags
 * @param {Set<string>} selectedFlagDatasets
 * @returns {number}
 */
export function countFlagsForSelectedDatasets(flags, selectedFlagDatasets) {
  if (!flags || !selectedFlagDatasets || selectedFlagDatasets.size === 0) {
    return 0;
  }

  let total = 0;

  if (selectedFlagDatasets.has('wells')) {
    if (flags._wellFlagsCount !== undefined) {
      total += flags._wellFlagsCount;
    } else {
      if (flags.inactiveWells) total += 1;
      if (flags.inactiveWellsOver5Years) total += 1;
    }
  }
  if (selectedFlagDatasets.has('production')) {
    if (flags.flaringIntensity) total += 1;
    if (flags.fuelGasIntensity) total += 1;
  }
  if (selectedFlagDatasets.has('inspections') && flags.inspectionsCompliance) {
    total += 1;
  }
  if (selectedFlagDatasets.has('violations')) {
    if (flags.violationsCount) total += 1;
    if (flags.violationsMajor) total += 1;
    if (flags.violationsReinspection) total += 1;
  }
  if (selectedFlagDatasets.has('severances')) {
    if (flags.severancesCount) total += 1;
    if (flags.severancesResolved) total += 1;
  }
  if (selectedFlagDatasets.has('spills')) {
    if (flags.spillsCount) total += 1;
    if (flags.spillsWaterImpact) total += 1;
  }

  return total;
}

/**
 * Whether a flags object has any flag from the selected datasets.
 * @param {Object} flags
 * @param {Set<string>} selectedFlagDatasets
 * @returns {boolean}
 */
export function flagsMatchSelectedDatasets(flags, selectedFlagDatasets) {
  return countFlagsForSelectedDatasets(flags, selectedFlagDatasets) > 0;
}

/**
 * Resolve cached flag entry for a lease (county-lease key preferred).
 * @param {Map|null} redFlagsCache
 * @param {string} leaseKey
 * @param {string|null} county
 * @returns {Object|null}
 */
export function getCachedLeaseFlagsEntry(redFlagsCache, leaseKey, county = null) {
  if (!redFlagsCache || !leaseKey) {
    return null;
  }

  const tryKeys = [];
  if (county) {
    const raw = String(county).trim();
    tryKeys.push(`${raw}-${leaseKey}`);
    tryKeys.push(`${raw.toUpperCase()}-${leaseKey}`);
  }
  tryKeys.push(leaseKey);

  for (const key of tryKeys) {
    const cached = redFlagsCache.get(key);
    if (cached?.flags) {
      return cached;
    }
  }

  for (const [cacheKey, cached] of redFlagsCache.entries()) {
    if (cacheKey === leaseKey || cacheKey.endsWith(`-${leaseKey}`)) {
      if (cached?.flags) {
        return cached;
      }
    }
  }

  return null;
}

/**
 * Flag count for a lease using selected datasets.
 */
export function getLeaseSelectedFlagCount(redFlagsCache, leaseKey, county, selectedFlagDatasets) {
  const cached = getCachedLeaseFlagsEntry(redFlagsCache, leaseKey, county);
  if (!cached) return 0;
  return countFlagsForSelectedDatasets(cached.flags, selectedFlagDatasets);
}

/**
 * Per-county flag totals + grand total for selected datasets.
 * @returns {{ byCounty: Map<string, number>, total: number }}
 */
export function getCountySelectedFlagMetrics(redFlagsCache, selectedFlagDatasets) {
  const byCounty = new Map();
  let total = 0;
  if (!redFlagsCache) {
    return { byCounty, total };
  }

  const seenLeaseCounty = new Set();
  for (const [cacheKey, cached] of redFlagsCache.entries()) {
    if (!cached?.flags) continue;
    const county = cached.county ? String(cached.county).trim().toUpperCase() : null;
    const leaseKey = cached.leaseKey || cacheKey;
    const dedupeKey = `${county || ''}|${leaseKey}`;
    if (seenLeaseCounty.has(dedupeKey)) continue;
    seenLeaseCounty.add(dedupeKey);

    const count = countFlagsForSelectedDatasets(cached.flags, selectedFlagDatasets);
    if (count <= 0) continue;
    total += count;
    if (county) {
      byCounty.set(county, (byCounty.get(county) || 0) + count);
    }
  }

  return { byCounty, total };
}

/**
 * Whether a lease has flags from any selected dataset.
 * @param {Map|null} redFlagsCache
 * @param {string} leaseKey
 * @param {string|null} county
 * @param {Set<string>} selectedFlagDatasets
 * @returns {boolean}
 */
export function leaseHasSelectedFlags(redFlagsCache, leaseKey, county, selectedFlagDatasets) {
  if (!redFlagsCache || !leaseKey) {
    return false;
  }

  const tryKeys = [];
  if (county) {
    const raw = String(county).trim();
    tryKeys.push(`${raw}-${leaseKey}`);
    tryKeys.push(`${raw.toUpperCase()}-${leaseKey}`);
  }
  tryKeys.push(leaseKey);

  for (const key of tryKeys) {
    const cached = redFlagsCache.get(key);
    if (cached?.flags && flagsMatchSelectedDatasets(cached.flags, selectedFlagDatasets)) {
      return true;
    }
  }

  // Fallback: any county-lease entry for this leaseKey
  for (const [cacheKey, cached] of redFlagsCache.entries()) {
    if (cacheKey === leaseKey || cacheKey.endsWith(`-${leaseKey}`)) {
      if (cached?.flags && flagsMatchSelectedDatasets(cached.flags, selectedFlagDatasets)) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Whether a county has any lease with flags from selected datasets.
 * @param {Map|null} redFlagsCache
 * @param {string} countyName
 * @param {Set<string>} selectedFlagDatasets
 * @returns {boolean}
 */
export function countyHasSelectedFlags(redFlagsCache, countyName, selectedFlagDatasets) {
  if (!redFlagsCache || !countyName) {
    return false;
  }

  const normalized = String(countyName).trim().toUpperCase();
  for (const cached of redFlagsCache.values()) {
    if (cached?.county && String(cached.county).trim().toUpperCase() === normalized) {
      if (cached.flags && flagsMatchSelectedDatasets(cached.flags, selectedFlagDatasets)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Resolve lease key + county from a well row for flag filtering.
 * @param {Object} well
 * @param {Function} generateLeaseKey
 * @returns {{ leaseKey: string, county: string|null }}
 */
export function getWellLeaseIdentity(well, generateLeaseKey) {
  const district = well['District'] || well.district || '00';
  const leaseNumber = well['Lease Number'] || well.lease_number || null;
  const leaseName = well['Lease Name'] || well.lease_name || null;
  const county = well['County'] || well.county || null;
  return {
    leaseKey: generateLeaseKey(district, leaseNumber, leaseName),
    county: county ? String(county).trim() : null,
  };
}
